import React, { useState } from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Box, CircularProgress, Alert } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import publisherBillingService, { PublisherBillingData } from '../services/publisherBillingService';
import { publisherService } from '../services/publisherService';
import SettingsBackButton from '../components/common/SettingsBackButton';

// Publisher List Component with Accordion
const PublisherList: React.FC<{
  publishers: PublisherBillingData[];
  formatCurrency: (amount: number) => string;
  formatDate: (dateString: string | null) => string;
}> = ({ publishers, formatCurrency, formatDate }) => {
  const [openPublishers, setOpenPublishers] = useState<Set<string>>(new Set());

  const togglePublisher = (publisherId: string) => {
    setOpenPublishers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(publisherId)) {
        newSet.delete(publisherId);
      } else {
        newSet.add(publisherId);
      }
      return newSet;
    });
  };

  return (
    <div className="pub-list">
      {publishers.map((publisher: PublisherBillingData) => {
        const isOpen = openPublishers.has(publisher.publisherId);
        return (
          <div key={publisher.publisherId} className={`pub-row ${isOpen ? 'open' : ''}`}>
            <div className="pub-header" onClick={() => togglePublisher(publisher.publisherId)}>
              <div className="pub-info">
                <div className="pub-name">{publisher.publisherName}</div>
                <div className="pub-email">{publisher.publisherEmail}</div>
              </div>
              <div className="pub-right">
                <span className="pub-amount">{formatCurrency(publisher.totalEarnings)}</span>
                <span className="pub-count">{publisher.totalPlacements} Placements</span>
                <div className="pub-chevron">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </div>
              </div>
            </div>
            <div className="pub-body">
              <div className="pub-body-inner">
                <div className="pub-body-content">
                  {/* Monthly Summary */}
                  {publisher.monthlyTotals.length > 0 && (
                    <div style={{ marginBottom: '22px' }}>
                      <h3 className="monthly-title">Monthly Summary</h3>
                      <div className="monthly-grid">
                        {publisher.monthlyTotals.map((monthData, index) => (
                          <div key={index} className="monthly-card">
                            <div className="mc-month">{monthData.month} {monthData.year}</div>
                            <div className="mc-amount">{formatCurrency(monthData.total)}</div>
                            <div className="mc-placements">{monthData.count} placements</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Placement Details */}
                  <div>
                    <h3 className="details-title">Placement Details</h3>
                    <div className="details-table-wrap">
                      <div className="dtscroll">
                        <table className="dtable">
                          <thead>
                            <tr>
                              <th>Placement ID</th>
                              <th>Order Date</th>
                              <th>Site URL</th>
                              <th>Client</th>
                              <th>Project</th>
                              <th>Type</th>
                              <th>Earnings</th>
                            </tr>
                          </thead>
                          <tbody>
                            {publisher.placements.map((placement) => (
                              <tr key={placement.placementId}>
                                <td><span className="c-id">{placement.placementId}</span></td>
                                <td><span className="c-date">{formatDate(placement.orderDate)}</span></td>
                                <td>
                                  <a href={placement.siteUrl || '#'} className="c-url" target="_blank" rel="noopener noreferrer">
                                    {placement.siteUrl}
                                  </a>
                                </td>
                                <td><span className="c-client">{placement.clientName}</span></td>
                                <td><span className="c-project">{placement.projectName}</span></td>
                                <td>
                                  <span className={`type-badge ${
                                    placement.submissionType === 'GBS' ? 'tb-gbs' : 
                                    placement.submissionType.includes('EXTERNAL') ? 'tb-ext' : 
                                    'tb-pr'
                                  }`}>
                                    {placement.submissionType}
                                  </span>
                                </td>
                                <td><span className="c-earn">{formatCurrency(placement.basePrice)}</span></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const AdminBillingPage: React.FC = () => {
  const currentYear = new Date().getFullYear();

  const [selectedPublisher, setSelectedPublisher] = useState<string>('all');
  const [selectedMonth, setSelectedMonth] = useState<number | undefined>(undefined);
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);

  // Fetch all publishers for filter
  const { data: publishersData } = useQuery({
    queryKey: ['publishers-for-billing'],
    queryFn: () => publisherService.getPublishers({ limit: 1000 }),
  });

  // Fetch billing data
  const { data: billingData, isLoading, error } = useQuery({
    queryKey: ['admin-billing', selectedPublisher, selectedMonth, selectedYear],
    queryFn: () => publisherBillingService.getAdminBilling(
      selectedPublisher === 'all' ? undefined : selectedPublisher,
      selectedMonth,
      selectedYear
    ),
  });

  const publishers = publishersData?.data || [];
  const billing = billingData?.data;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const generatePDF = () => {
    if (!billing) return;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Title
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('Publisher Billing Report', pageWidth / 2, 15, { align: 'center' });
    
    // Report Info
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const reportDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    doc.text(`Generated: ${reportDate}`, 14, 25);
    
    let filterText = 'Filter: ';
    if (selectedPublisher !== 'all') {
      const pub = publishers.find((p: any) => p.id === selectedPublisher);
      filterText += `Publisher: ${pub?.publisherName || 'Unknown'}`;
    } else {
      filterText += 'All Publishers';
    }
    if (selectedMonth) {
      filterText += `, Month: ${new Date(2024, selectedMonth - 1, 1).toLocaleString('default', { month: 'long' })}`;
    }
    if (selectedYear) {
      filterText += `, Year: ${selectedYear}`;
    }
    doc.text(filterText, 14, 30);
    
    let yPos = 40;

    {
      // Summary Section
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Summary', 14, yPos);
      yPos += 7;
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Total Revenue: ${formatCurrency(billing.totalEarnings)}`, 14, yPos);
      yPos += 5;
      doc.text(`Total Placements: ${billing.totalPlacements}`, 14, yPos);
      yPos += 5;
      doc.text(`Total Publishers Cost: ${formatCurrency(billing.totalPublisherCost)}`, 14, yPos);
      yPos += 10;

      // Publisher Details
      billing.publishers.forEach((publisher: PublisherBillingData, index: number) => {
        // Skip publishers that don't match the filter for publisher-specific reports
        if (selectedPublisher !== 'all' && publisher.publisherId !== selectedPublisher) {
          return;
        }

        // Check if we need a new page
        if (yPos > 250) {
          doc.addPage();
          yPos = 20;
        }

        // Publisher Header
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text(`${publisher.publisherName}`, 14, yPos);
        yPos += 5;
        
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.text(`Email: ${publisher.publisherEmail || 'N/A'}`, 14, yPos);
        yPos += 4;
        doc.text(`Total Earnings: ${formatCurrency(publisher.totalEarnings)} | Placements: ${publisher.totalPlacements}`, 14, yPos);
        yPos += 7;

        // Monthly Totals Table
        if (publisher.monthlyTotals.length > 0) {
          autoTable(doc, {
            startY: yPos,
            head: [['Month', 'Year', 'Earnings', 'Placements']],
            body: publisher.monthlyTotals.map(m => [
              m.month,
              m.year.toString(),
              formatCurrency(m.total),
              m.count.toString()
            ]),
            theme: 'grid',
            headStyles: { fillColor: [79, 124, 255], fontSize: 9 },
            bodyStyles: { fontSize: 8 },
            margin: { left: 14, right: 14 },
            tableWidth: 'auto',
          });
          yPos = (doc as any).lastAutoTable.finalY + 7;
        }

        // Placement Details Table
        if (publisher.placements.length > 0) {
          autoTable(doc, {
            startY: yPos,
            head: [['Site URL', 'Placement ID', 'Order Date', 'Client', 'Project', 'Type', 'Earnings']],
            body: publisher.placements.map(p => [
              p.siteUrl || '',
              p.placementId,
              formatDate(p.orderDate),
              p.clientName,
              p.projectName,
              p.submissionType,
              formatCurrency(p.basePrice)
            ]),
            theme: 'striped',
            headStyles: { fillColor: [79, 124, 255], fontSize: 8 },
            bodyStyles: { fontSize: 7 },
            margin: { left: 14, right: 14 },
            tableWidth: 'auto',
          });
          yPos = (doc as any).lastAutoTable.finalY + 10;
        }

        // Add some space between publishers
        yPos += 5;
      });
    }

    // Generate filename
    let filename = 'publisher-billing';
    if (selectedPublisher !== 'all') {
      const pub = publishers.find((p: any) => p.id === selectedPublisher);
      filename += `-${pub?.publisherName.replace(/\s+/g, '-').toLowerCase() || 'unknown'}`;
    }
    if (selectedMonth) {
      filename += `-${new Date(2024, selectedMonth - 1, 1).toLocaleString('default', { month: 'long' }).toLowerCase()}`;
    }
    if (selectedYear) {
      filename += `-${selectedYear}`;
    }
    filename += '.pdf';

    doc.save(filename);
  };

  const handleDownloadPDF = () => {
    generatePDF();
  };

  return (
    <Box className="page">
      <style>{`
        @keyframes fadeUp { from { opacity: 0; transform: translateY(5px); } to { opacity: 1; transform: translateY(0); } }
        
        .page { padding: 22px; display: flex; flex-direction: column; gap: 18px; }
        
        .ph { display: flex; flex-direction: column; gap: 3px; }
        .ph-title { font-size: 19px; font-weight: 700; letter-spacing: -0.4px; color: #111827; }
        .ph-sub { font-size: 13px; color: #6B7280; }
        
        .filter-card {
          background: #FFFFFF; border: 1px solid #E5E7EB;
          border-radius: 8px; box-shadow: 0 1px 2px rgba(0,0,0,0.05);
          padding: 16px 18px;
          display: flex; align-items: flex-end; gap: 12px; flex-wrap: wrap;
        }
        .filter-section-label { font-size: 10.5px; font-weight: 600; color: #9CA3AF; letter-spacing: 0.6px; text-transform: uppercase; margin-bottom: 2px; }
        .fc-left { display: flex; align-items: flex-end; gap: 12px; flex-wrap: wrap; flex: 1; }
        .fc-right { margin-left: auto; flex-shrink: 0; }
        .filter-label-group { display: flex; flex-direction: column; gap: 4px; }
        
        .fsel {
          appearance: none; border: 1px solid #E5E7EB; border-radius: 6px;
          padding: 8px 30px 8px 12px; font-family: inherit; font-size: 13px;
          color: #374151; background: #FFFFFF; outline: none; cursor: pointer;
          transition: all 0.14s;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%239CA3AF' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
          background-repeat: no-repeat; background-position: right 10px center;
        }
        .fsel:focus { border-color: #4F46E5; box-shadow: 0 0 0 3px rgba(79,70,229,0.08); }
        
        .btn { display: inline-flex; align-items: center; gap: 6px; padding: 7px 14px; border-radius: 6px; font-family: inherit; font-size: 12.5px; font-weight: 500; cursor: pointer; border: none; transition: all 0.14s; white-space: nowrap; line-height: 1; }
        .btn svg { width: 13px; height: 13px; flex-shrink: 0; }
        .btn-primary { background: #4F46E5; color: #fff; box-shadow: 0 1px 2px rgba(79,70,229,0.25); }
        .btn-primary:hover { background: #4338CA; box-shadow: 0 3px 8px rgba(79,70,229,0.3); transform: translateY(-1px); }
        
        .stats-row { display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; }
        .gcard {
          border-radius: 12px; padding: 24px 26px;
          position: relative; overflow: hidden;
          box-shadow: 0 4px 20px rgba(0,0,0,0.12);
          transition: transform 0.18s, box-shadow 0.18s; cursor: default;
        }
        .gcard:hover { transform: translateY(-2px); box-shadow: 0 8px 28px rgba(0,0,0,0.16); }
        .gc-earnings { background: linear-gradient(135deg, #6D28D9 0%, #5B5BD6 40%, #4F46E5 100%); }
        .gc-placements { background: linear-gradient(135deg, #DB2777 0%, #EC4899 50%, #F43F5E 100%); }
        .gc-publishers { background: linear-gradient(135deg, #06B6D4 0%, #0EA5E9 60%, #22D3EE 100%); }
        
        .gcard-inner { position: relative; z-index: 1; }
        .gcard-top { display: flex; align-items: center; gap: 10px; margin-bottom: 14px; }
        .gcard-icon {
          width: 36px; height: 36px; border-radius: 9px;
          background: rgba(255,255,255,0.18);
          display: flex; align-items: center; justify-content: center;
        }
        .gcard-icon svg { width: 18px; height: 18px; color: #fff; }
        .gcard-label { font-size: 13px; font-weight: 600; color: rgba(255,255,255,0.9); letter-spacing: -0.1px; }
        .gcard-value { font-size: 30px; font-weight: 700; color: #fff; letter-spacing: -0.8px; line-height: 1; font-family: 'JetBrains Mono', monospace; }
        .gcard-sub { font-size: 12px; color: rgba(255,255,255,0.7); margin-top: 6px; font-weight: 400; }
        .gcard::before, .gcard::after { content: ''; position: absolute; border-radius: 50%; background: rgba(255,255,255,0.07); pointer-events: none; }
        .gcard::before { width: 140px; height: 140px; top: -40px; right: -30px; }
        .gcard::after { width: 90px; height: 90px; bottom: -20px; right: 40px; }
        
        .section-title { font-size: 15px; font-weight: 700; color: #111827; letter-spacing: -0.2px; }
        
        .pub-list { display: flex; flex-direction: column; gap: 0; background: #FFFFFF; border: 1px solid #E5E7EB; border-radius: 8px; box-shadow: 0 1px 2px rgba(0,0,0,0.05); overflow: hidden; }
        .pub-row { border-bottom: 1px solid #F3F4F6; }
        .pub-row:last-child { border-bottom: none; }
        .pub-header {
          display: flex; align-items: center; gap: 14px;
          padding: 16px 20px; cursor: pointer;
          transition: background 0.1s; user-select: none;
        }
        .pub-header:hover { background: #F9FAFB; }
        .pub-info { flex: 1; min-width: 0; }
        .pub-name { font-size: 14px; font-weight: 600; color: #111827; letter-spacing: -0.1px; }
        .pub-email { font-size: 12px; color: #6B7280; margin-top: 2px; }
        .pub-right { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
        .pub-amount {
          padding: 5px 13px; border-radius: 99px;
          font-size: 12.5px; font-weight: 700;
          background: linear-gradient(135deg, #6D28D9, #4F46E5);
          color: #fff; font-family: 'JetBrains Mono', monospace;
          box-shadow: 0 2px 6px rgba(79,70,229,0.25);
        }
        .pub-count {
          padding: 5px 13px; border-radius: 99px;
          font-size: 12px; font-weight: 600;
          background: linear-gradient(135deg, #7C3AED, #8B5CF6);
          color: #fff;
          box-shadow: 0 2px 6px rgba(124,58,237,0.2);
        }
        .pub-chevron {
          width: 28px; height: 28px; border-radius: 5px;
          display: flex; align-items: center; justify-content: center;
          color: #9CA3AF; transition: all 0.22s;
        }
        .pub-chevron svg { width: 14px; height: 14px; transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1); }
        .pub-row.open .pub-chevron svg { transform: rotate(180deg); }
        .pub-header:hover .pub-chevron { color: #374151; background: #F3F4F6; }
        
        .pub-body {
          display: grid; grid-template-rows: 0fr;
          transition: grid-template-rows 0.28s cubic-bezier(0.4, 0, 0.2, 1);
          background: #F9FAFB;
          border-top: 0px solid #F3F4F6;
        }
        .pub-row.open .pub-body {
          grid-template-rows: 1fr;
          border-top-width: 1px;
        }
        .pub-body-inner { overflow: hidden; }
        .pub-body-content { padding: 20px 20px 22px; }
        
        .monthly-title { font-size: 13px; font-weight: 700; color: #111827; margin-bottom: 12px; letter-spacing: -0.1px; }
        .monthly-grid { display: flex; gap: 12px; flex-wrap: wrap; margin-bottom: 22px; }
        .monthly-card {
          background: #FFFFFF; border: 1px solid #E5E7EB;
          border-radius: 8px; padding: 14px 18px;
          box-shadow: 0 1px 2px rgba(0,0,0,0.05); min-width: 170px;
          transition: box-shadow 0.15s; cursor: default;
        }
        .monthly-card:hover { box-shadow: 0 1px 3px rgba(0,0,0,0.07), 0 1px 2px rgba(0,0,0,0.04); }
        .mc-month { font-size: 12px; color: #6B7280; margin-bottom: 6px; font-weight: 500; }
        .mc-amount { font-size: 17px; font-weight: 700; color: #4F46E5; font-family: 'JetBrains Mono', monospace; letter-spacing: -0.3px; }
        .mc-placements { font-size: 11.5px; color: #9CA3AF; margin-top: 4px; }
        
        .details-title { font-size: 13px; font-weight: 700; color: #111827; margin-bottom: 12px; letter-spacing: -0.1px; }
        .details-table-wrap { background: #FFFFFF; border: 1px solid #E5E7EB; border-radius: 8px; overflow: hidden; box-shadow: 0 1px 2px rgba(0,0,0,0.05); }
        .dtscroll { overflow-x: auto; }
        .dtable { width: 100%; border-collapse: collapse; min-width: 760px; }
        .dtable thead th {
          text-align: left; font-size: 10.5px; font-weight: 600; color: #9CA3AF;
          letter-spacing: 0.5px; text-transform: uppercase; padding: 9px 14px;
          border-bottom: 1px solid #E5E7EB; background: #FAFAFA;
          white-space: nowrap;
        }
        .dtable thead th:first-child { padding-left: 16px; }
        .dtable thead th:last-child { text-align: right; padding-right: 16px; }
        .dtable tbody tr { transition: background 0.08s; }
        .dtable tbody tr:hover { background: #FAFBFF; }
        .dtable tbody td {
          padding: 10px 14px; font-size: 12.5px;
          border-bottom: 1px solid #F3F4F6;
          vertical-align: middle; white-space: nowrap;
        }
        .dtable tbody tr:last-child td { border-bottom: none; }
        .dtable tbody td:first-child { padding-left: 16px; }
        .dtable tbody td:last-child { text-align: right; padding-right: 16px; }
        
        .c-id { font-family: 'JetBrains Mono', monospace; font-size: 11.5px; font-weight: 500; color: #6B7280; }
        .c-date { font-size: 12.5px; color: #374151; }
        .c-url { color: #4F46E5; font-size: 12.5px; font-weight: 450; text-decoration: none; }
        .c-url:hover { text-decoration: underline; }
        .c-client { font-size: 12.5px; font-weight: 500; color: #111827; }
        .c-project { font-size: 12px; color: #6B7280; max-width: 160px; overflow: hidden; text-overflow: ellipsis; display: block; }
        .c-earn { font-family: 'JetBrains Mono', monospace; font-size: 13px; font-weight: 700; color: #111827; }
        
        .type-badge { display: inline-block; padding: 3px 9px; border-radius: 5px; font-size: 11px; font-weight: 700; letter-spacing: 0.3px; }
        .tb-gbs { background: #4F46E5; color: #fff; }
        .tb-ext { background: #0D9488; color: #fff; }
        .tb-pr { background: #7C3AED; color: #fff; }
        
        .dtable tfoot td {
          padding: 10px 14px; font-size: 12.5px;
          border-top: 2px solid #E5E7EB;
          font-weight: 600; color: #111827;
        }
        .dtable tfoot td:first-child { padding-left: 16px; }
        .dtable tfoot td:last-child { text-align: right; padding-right: 16px; font-family: 'JetBrains Mono', monospace; font-size: 13.5px; font-weight: 700; color: #4F46E5; }
      `}</style>

      <SettingsBackButton />

      {/* Page header */}
      <div className="ph">
        <h1 className="ph-title">Publisher Billing Management</h1>
        <p className="ph-sub">View and manage billing for all publishers</p>
      </div>

      {/* Filter card */}
      <div className="filter-card">
        <div className="fc-left">
          <div className="filter-label-group">
            <span className="filter-section-label">Filters</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <select
                className="fsel"
                value={selectedPublisher}
                onChange={(e) => setSelectedPublisher(e.target.value)}
                style={{ minWidth: '200px' }}
              >
                <option value="all">All Publishers</option>
                {publishers.map((pub: any) => (
                  <option key={pub.id} value={pub.id}>
                    {pub.publisherName} {pub.email && `(${pub.email})`}
                  </option>
                ))}
              </select>
              <select
                className="fsel"
                value={selectedMonth || ''}
                onChange={(e) => setSelectedMonth(e.target.value ? Number(e.target.value) : undefined)}
                style={{ minWidth: '150px' }}
              >
                <option value="">Month</option>
                {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                  <option key={month} value={month}>
                    {new Date(2024, month - 1, 1).toLocaleString('default', { month: 'long' })}
                  </option>
                ))}
              </select>
              <select
                className="fsel"
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
              >
                {Array.from({ length: 5 }, (_, i) => currentYear - i).map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <div className="fc-right">
          <button className="btn btn-primary" onClick={handleDownloadPDF}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            Download PDF
          </button>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      )}

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load billing data. Please try again.
        </Alert>
      )}

      {/* Stats Cards */}
      {!isLoading && billing && (
        <>
          <div className="stats-row">
            <div className="gcard gc-earnings">
              <div className="gcard-inner">
                <div className="gcard-top">
                  <div className="gcard-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="12" y1="1" x2="12" y2="23"/>
                      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                    </svg>
                  </div>
                  <span className="gcard-label">Total Revenue</span>
                </div>
                <div className="gcard-value">{formatCurrency(billing.totalEarnings)}</div>
                <div className="gcard-sub">Across all publishers</div>
              </div>
            </div>
            <div className="gcard gc-placements">
              <div className="gcard-inner">
                <div className="gcard-top">
                  <div className="gcard-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                      <polyline points="14 2 14 8 20 8"/>
                      <line x1="16" y1="13" x2="8" y2="13"/>
                      <line x1="16" y1="17" x2="8" y2="17"/>
                      <polyline points="10 9 9 9 8 9"/>
                    </svg>
                  </div>
                  <span className="gcard-label">Total Placements</span>
                </div>
                <div className="gcard-value">{billing.totalPlacements}</div>
                <div className="gcard-sub">Completed placements</div>
              </div>
            </div>
            <div className="gcard gc-publishers">
              <div className="gcard-inner">
                <div className="gcard-top">
                  <div className="gcard-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="12" y1="1" x2="12" y2="23"/>
                      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                    </svg>
                  </div>
                  <span className="gcard-label">Total Publishers Cost</span>
                </div>
                <div className="gcard-value">{formatCurrency(billing.totalPublisherCost)}</div>
                <div className="gcard-sub">Publisher base price</div>
              </div>
            </div>
          </div>

          {/* Publisher Billing Details */}
          <div>
            <h2 className="section-title" style={{ marginBottom: '14px' }}>Publisher Billing Details</h2>
            
            {billing.publishers.length === 0 ? (
              <Alert severity="info">
                No billing data found for the selected filters.
              </Alert>
            ) : (
              <PublisherList publishers={billing.publishers} formatCurrency={formatCurrency} formatDate={formatDate} />
            )}
          </div>
        </>
      )}
    </Box>
  );
};

export default AdminBillingPage;
